import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import AdminLayout from "@/components/AdminLayout";
import { OpBadge, PayBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { naira, fmtDate } from "@/lib/format";

const STATUSES = ["all", "Quote Requested", "Quote Sent", "Quote Accepted", "Awaiting Payment",
  "Payment Confirmed", "Scheduled", "Preparing for Dispatch", "Dispatched", "In Transit", "Delivered", "Completed", "Cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    const { data } = await api.get("/admin/orders", { params });
    setOrders(data);
  }, [q, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-bold text-slate-900">Orders</h1>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search company, phone, email or order number" className="pl-10" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="orders-status-filter" className="sm:w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 rounded-lg border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right">Outstanding</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {orders.map((o) => (
              <tr key={o.id} className="hover:bg-[#F9FAFB]">
                <td className="px-4 py-3">
                  <Link to={`/admin/orders/${o.id}`} data-testid={`admin-order-${o.order_number}`} className="font-semibold text-[#0A4F35] hover:underline">{o.order_number}</Link>
                  <div className="text-xs text-slate-400">{fmtDate(o.created_at)}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{o.company_name}</td>
                <td className="px-4 py-3"><OpBadge status={o.operational_status} /></td>
                <td className="px-4 py-3"><PayBadge status={o.financials.payment_status} /></td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">{naira(o.financials.total)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-medium text-[#DC2626]">
                  {o.financials.outstanding > 0 ? naira(o.financials.outstanding) : "—"}
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No orders found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
